import { FC, useCallback } from 'react';
import { format } from 'date-fns';
import { StatusReport } from 'models/softwareUpate';
import Tooltip from 'components/UI/Tooltip';
import { HeaderCell } from 'components/UI/Table/SortableTable';
import TableBody from 'components/UI/Table/TableBody';
import Table from 'components/UI/Table';
import TableHeader from 'components/UI/Table/TableHeader';
import TableRow from 'components/UI/Table/TableRow';
import TableHeaderCell from 'components/UI/Table/TableHeaderCell';
import { FormattedMessage } from 'react-intl';
import TableBodyCell from 'components/UI/Table/TableBodyCell';
import Icon, { IconType } from 'components/UI/Icon';
import { defaultTheme } from 'styles/ThemeWrapper';

const headerCells: HeaderCell[] = [
  {
    title: 'connectivity.software_history_update_status_time',
    sortable: true,
  },
  {
    title: 'connectivity.software_history_update_status_type',
    sortable: false,
  },
  {
    title: 'connectivity.software_history_update_status_message',
    sortable: false,
  },
];

type Props = {
  statusResports: StatusReport[];
  isLoading: boolean;
};

const SoftwareStatusTable: FC<Props> = ({ statusResports, isLoading }) => {
  // Render tooltip
  const renderTooltip = useCallback((text: string) => {
    if (text?.length > 50) {
      return (
        <Tooltip paragraph={text}>
          {text.substring(0, 50).concat('...')}
        </Tooltip>
      );
    }
    return text;
  }, []);

  // Render status
  const renderEventType = useCallback((event: string) => {
    let iconColor = 'red';
    let iconType = IconType.Close;
    switch (event) {
      case 'error':
        iconColor = defaultTheme.colors.error;
        iconType = IconType.Inactive;
        break;
      case 'retrieved':
        iconColor = defaultTheme.colors.warning;
        iconType = IconType.Info;
        break;
      case 'running':
        iconColor = defaultTheme.colors.warning;
        iconType = IconType.Info;
        break;
      case 'download':
        iconColor = defaultTheme.colors.warning;
        iconType = IconType.Download;
        break;
      case 'downloaded':
        iconColor = defaultTheme.colors.success;
        iconType = IconType.Download;
        break;
      case 'finished':
        iconColor = defaultTheme.colors.success;
        iconType = IconType.Active;
        break;
      default:
        iconColor = defaultTheme.colors.grey3;
        iconType = IconType.Active;
    }

    return (
      <Tooltip paragraph={event}>
        <Icon type={iconType} size={'small'} color={iconColor} />
      </Tooltip>
    );
    // <IconCircle status={iconStatus} text={iconText} size="16px" />;
  }, []);

  if (!statusResports || statusResports?.length === 0) {
    return null;
  }

  // Table rows
  const bodyRows = statusResports.map(({ type, message, reportedAt }) => {
    return {
      reported:
        reportedAt && reportedAt > 0
          ? format(Number(reportedAt), 'yyyy-MM-dd HH:mm:ss')
          : '-',
      type: renderEventType(type),
      message: renderTooltip(message),
    };
  });

  if (isLoading) {
    return null;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {headerCells.map((cell, index) => {
            return (
              <TableHeaderCell key={index}>
                {cell.title && <FormattedMessage id={cell.title} />}
              </TableHeaderCell>
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {bodyRows.map((item, rowIndex) => {
          return (
            <TableRow key={rowIndex}>
              {Object.entries(item).map(([name, value], colIndex) => (
                <TableBodyCell key={colIndex} name={name} value={value} />
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default SoftwareStatusTable;
